import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DotsIcon } from '../../../svg';
import { capitalize } from '../../../utils/string';
import { getConversationName,getConversationPicture,} from "../../../utils/chat";
import { aiAgentAPI } from '../../../lib/api';
import { getConversationMessages, getConversations } from '../../../features/chatSlice';
import { messagesAPI } from '../../../lib/api';

const ChatHeader = ({online}) => {
    const dispatch = useDispatch();
    const {activeConversation} = useSelector((state) => state.chat)
    const { user } = useSelector((state) => state.user);
    const [sessionAIEnabled, setSessionAIEnabled] = useState(true);
    const [globalAIEnabled, setGlobalAIEnabled] = useState(true);
    const [aiLoading, setAiLoading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [tagsLoading, setTagsLoading] = useState(false);
    const [showTagInput, setShowTagInput] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const menuButtonRef = useRef(null);
    const menuRef = useRef(null);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        const sessionId = activeConversation?._id;
        if (!sessionId) return;
        let isMounted = true;
        aiAgentAPI.getSessionStatus(sessionId)
            .then((res) => {
                if (!isMounted) return;
                const data = res.data || {};
                setSessionAIEnabled(data.sessionAIEnabled !== false);
                setGlobalAIEnabled(data.globalAIEnabled !== false);
            })
            .catch(() => {});
        return () => {
            isMounted = false;
        };
    }, [activeConversation?._id]);

    useEffect(() => {
        const sessionId = activeConversation?._id;
        if (!sessionId) {
            setTags([]);
            return;
        }
        const initialTags = activeConversation?.rawSession?.tags || activeConversation?.tags || [];
        setTags(Array.isArray(initialTags) ? initialTags : []);
        setTagsLoading(true);
        messagesAPI.getSessionTags(sessionId)
            .then((res) => {
                const data = res.data || {};
                if (Array.isArray(data.tags)) setTags(data.tags);
            })
            .catch(() => {})
            .finally(() => setTagsLoading(false));
    }, [activeConversation?._id]);

    const toggleSessionAI = async () => {
        const sessionId = activeConversation?._id;
        if (!sessionId) return;
        const next = !sessionAIEnabled;
        setAiLoading(true);
        setSessionAIEnabled(next);
        try {
            const res = await aiAgentAPI.toggleSession(sessionId, next);
            const data = res.data || {};
            setSessionAIEnabled(data.sessionAIEnabled !== false);
            setGlobalAIEnabled(data.globalAIEnabled !== false);
        } catch (_err) {
            setSessionAIEnabled((prev) => !prev);
        } finally {
            setAiLoading(false);
        }
    };

    useEffect(() => {
        if (!menuOpen) return;
        const updateMenuPos = () => {
            const rect = menuButtonRef.current?.getBoundingClientRect();
            if (!rect) return;
            const menuWidth = menuRef.current?.offsetWidth || 160;
            const maxLeft = Math.max(8, window.innerWidth - menuWidth - 8);
            const desiredLeft = rect.right - menuWidth;
            setMenuPos({
                top: rect.bottom + 8,
                left: Math.min(maxLeft, Math.max(8, desiredLeft))
            });
        };
        const handleClickOutside = (e) => {
            if (menuRef.current?.contains(e.target)) return;
            if (menuButtonRef.current?.contains(e.target)) return;
            setMenuOpen(false);
        };
            updateMenuPos();
            requestAnimationFrame(updateMenuPos);
        window.addEventListener('resize', updateMenuPos);
        window.addEventListener('scroll', updateMenuPos, true);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('resize', updateMenuPos);
            window.removeEventListener('scroll', updateMenuPos, true);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuOpen]);

    useEffect(() => {
        if (!confirmOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setConfirmOpen(false);
        };
        const onClickOutside = (e) => {
            if (e.target === e.currentTarget) setConfirmOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('click', onClickOutside);
        };
    }, [confirmOpen]);

    const handleClearChat = async () => {
        const sessionId = activeConversation?._id;
        if (!sessionId || clearing) return;
        setClearing(true);
        try {
            await messagesAPI.clearSession(sessionId);
            const token = user?.token;
            if (token) {
                dispatch(getConversationMessages({ token, convo_id: sessionId }));
                dispatch(getConversations(token));
            }
        } catch (_err) {
            // noop
        } finally {
            setClearing(false);
            setMenuOpen(false);
            setConfirmOpen(false);
        }
    };

    const normalizeTag = (value) => String(value || '').trim().toLowerCase();

    const handleAddTag = async () => {
        const sessionId = activeConversation?._id;
        if (!sessionId) return;
        const nextTag = normalizeTag(tagInput);
        if (!nextTag || nextTag.length > 20) return;
        if (tags.includes(nextTag)) return;
        if (tags.length >= 10) return;
        setTagsLoading(true);
        try {
            const res = await messagesAPI.updateSessionTags(sessionId, [nextTag], []);
            const data = res.data || {};
            if (Array.isArray(data.tags)) setTags(data.tags);
            setTagInput('');
        } catch (_err) {
            // noop
        } finally {
            setTagsLoading(false);
        }
    };

    const handleRemoveTag = async (tag) => {
        const sessionId = activeConversation?._id;
        if (!sessionId || !tag) return;
        setTagsLoading(true);
        try {
            const res = await messagesAPI.updateSessionTags(sessionId, [], [tag]);
            const data = res.data || {};
            if (Array.isArray(data.tags)) setTags(data.tags);
        } catch (_err) {
            // noop
        } finally {
            setTagsLoading(false);
        }
    };

    const sessionInfo = activeConversation?.rawSession || {};
    const platformLabel = sessionInfo.platform || activeConversation?.platform || '';
    const platformUserName = sessionInfo.platformUserName || '';
    const platformUsername = sessionInfo.platformUsername || '';
    const platformUserId = sessionInfo.platformUserId || activeConversation?.platformUserId || '';
    const sessionId = sessionInfo.sessionId || activeConversation?._id || '';
    return (
        <div className='h-[59px] dark:bg-dark_bg_2 flex items-center p16 select-none'>
            <div className="w-full flex items-center justify-between">
                {/* left */}
                <div className="flex items-center gap-x-4">
                    <button className="btn">
                    <img
                        src={
                            activeConversation.isGroup ?
                            activeConversation.picture:
                            getConversationPicture(user, activeConversation.users)
                        }
                        alt=""
                        className="w-full h-full rounded-full object-cover"
                        />
                    </button>
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                            <h1 className="dark:text-white text-md font-bold">
                                {
                                    activeConversation.isGroup ?
                                    activeConversation.name:
                                    capitalize(getConversationName(user, activeConversation.users).split(" ")[0])}
                            </h1>
                            {platformLabel && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-green_1/30 bg-green_1/15 text-green_1 uppercase">
                                    {platformLabel}
                                </span>
                            )}
                            <button
                                type="button"
                                className="w-6 h-6 rounded-full border border-dark_border_1 text-[11px] flex items-center justify-center text-dark_text_2 hover:dark:bg-dark_hover_1"
                                title="معلومات العميل"
                                onClick={() => setInfoOpen(true)}
                            >
                                ?
                            </button>
                        </div>
                        <span className="text-xs dark:text-dark_svg_2">
                            {online ? "متصل" : ""}
                        </span>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            {tags.slice(0, 3).map((tag) => (
                                <span
                                    key={tag}
                                    className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green_1/15 text-green_1 border border-green_1/30"
                                >
                                    {tag}
                                    <button
                                        type="button"
                                        className="text-green_1/70 hover:text-red-300"
                                        onClick={() => handleRemoveTag(tag)}
                                        disabled={tagsLoading}
                                        aria-label="حذف وسم"
                                        title="حذف وسم"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            {tags.length > 3 && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-dark_border_1 dark:bg-dark_bg_2 text-dark_text_2">
                                    +{tags.length - 3}
                                </span>
                            )}
                            <button
                                type="button"
                                className="text-[10px] px-2 py-0.5 rounded-full border border-dark_border_1 dark:bg-dark_bg_2 text-dark_text_2 hover:dark:bg-dark_hover_1"
                                onClick={() => setShowTagInput((v) => !v)}
                                disabled={tagsLoading}
                            >
                                {showTagInput ? 'إغلاق' : 'إضافة وسم'}
                            </button>
                            {showTagInput && (
                                <div className="flex items-center gap-1">
                                    <input
                                        type="text"
                                        className="input !h-6 !text-[10px] !px-2 !py-0.5"
                                        placeholder="وسم"
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddTag();
                                            }
                                        }}
                                        disabled={tagsLoading}
                                    />
                                    <button
                                        type="button"
                                        className="text-[10px] px-2 py-0.5 rounded-full border border-dark_border_1 dark:bg-dark_bg_2 text-dark_text_2 hover:dark:bg-dark_hover_1"
                                        onClick={handleAddTag}
                                        disabled={tagsLoading}
                                    >
                                        إضافة
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* right */}
                <ul className="flex items-center gap-x-2.5">
                    <li className="relative">
                        <button
                            type="button"
                            onClick={toggleSessionAI}
                            disabled={aiLoading}
                            className={`px-2 py-1 rounded-full text-xs font-medium border transition ${
                                sessionAIEnabled
                                    ? 'bg-green_1/20 border-green_1 text-green_1'
                                    : 'dark:bg-dark_bg_3 border-dark_border_1 text-dark_text_2'
                            }`}
                            title={globalAIEnabled ? (sessionAIEnabled ? 'AI مفعّل للجلسة' : 'AI معطّل للجلسة') : 'AI معطّل عالمياً'}
                        >
                            {sessionAIEnabled ? 'AI On' : 'AI Off'}
                        </button>
                    </li>
                    <li>
                        <button
                            ref={menuButtonRef}
                            className={`btn ${menuOpen ? "bg-dark_hover_1" : ""}`}
                            onClick={() => setMenuOpen((v) => !v)}
                            title="خيارات"
                            type="button"
                        >
                            <DotsIcon className="dark:fill-dark_svg_1"/>
                        </button>
                        {menuOpen && (
                            <div
                                ref={menuRef}
                                className="fixed z-50 min-w-[160px] rounded-lg border border-dark_border_1 dark:bg-dark_bg_2 shadow-lg"
                                style={{ top: menuPos.top, left: menuPos.left }}
                            >
                                <button
                                    type="button"
                                    className="w-full text-start px-3 py-2 text-sm text-red-300 hover:dark:bg-dark_hover_1"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        setConfirmOpen(true);
                                    }}
                                    disabled={clearing}
                                >
                                    {clearing ? 'جاري الحذف...' : 'حذف المحادثة'}
                                </button>
                            </div>
                        )}
                    </li>




                </ul>
            </div>
            {confirmOpen && (
                <div
                    className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setConfirmOpen(false);
                    }}
                >
                    <div className="w-full max-w-md dark:bg-dark_bg_2 border border-dark_border_1 rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-dark_border_1">
                            <h3 className="text-dark_text_1 font-semibold">تأكيد حذف المحادثة</h3>
                            <p className="text-sm text-dark_text_2 mt-2">
                                هذا الإجراء سيحذف جميع رسائل هذه المحادثة نهائياً ولا يمكن التراجع عنه.
                            </p>
                        </div>
                        <div className="p-4 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                className="px-3 py-2 rounded-lg border border-dark_border_1 text-sm text-dark_text_2 hover:dark:bg-dark_hover_1"
                                onClick={() => setConfirmOpen(false)}
                                disabled={clearing}
                            >
                                إلغاء
                            </button>
                            <button
                                type="button"
                                className="px-3 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-500"
                                onClick={handleClearChat}
                                disabled={clearing}
                            >
                                {clearing ? 'جاري الحذف...' : 'تأكيد الحذف'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {infoOpen && (
                <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={(e) => {
                    if (e.target === e.currentTarget) setInfoOpen(false);
                }}>
                    <div className="w-full max-w-md dark:bg-dark_bg_2 border border-dark_border_1 rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-dark_border_1 flex items-center justify-between">
                            <h3 className="text-dark_text_1 font-semibold">معلومات العميل</h3>
                            <button className="w-8 h-8 rounded-lg hover:bg-dark_hover_1 text-dark_text_2" onClick={() => setInfoOpen(false)}>×</button>
                        </div>
                        <div className="p-4 text-sm text-dark_text_2 space-y-2">
                            {platformUserName && <div>الاسم: {platformUserName}</div>}
                            {platformUsername && <div>المستخدم: {platformUsername}</div>}
                            {platformUserId && <div>المعرّف: {platformUserId}</div>}
                            {sessionId && <div>الجلسة: {sessionId}</div>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ChatHeader;
